import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight } from "react-icons/fa";
import heroImage from "../../../assets/arvix/arvix_hero-min.png";
import { Reveal } from "../../../components/common";

const HeroSection = () => {
  return (
    <section
      className="relative pt-24 sm:pt-28 md:pt-36 pb-10 md:pb-14"
      itemScope
      itemType="https://schema.org/Organization"
    >
      {/* faint dotted texture, top-right only */}
      <div
        className="texture-dots pointer-events-none absolute top-0 right-0 h-72 w-72 opacity-60 [mask-image:radial-gradient(100%_100%_at_100%_0%,black,transparent)]"
        aria-hidden="true"
      />

      <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-8 xl:gap-14 items-center">
        {/* Copy */}
        <div className="max-w-2xl">
          <Reveal>
            <div className="flex items-center gap-3">
              <span className="tick" aria-hidden="true" />
              <p className="text-xs font-bold tracking-[0.25em] uppercase text-designer-dark/70">
                Creative studio · Est. 2018 · Chattogram, Bangladesh
              </p>
            </div>
          </Reveal>

          <Reveal delay={90}>
            <h1
              className="text-balance mt-5 font-display font-bold tracking-tight text-5xl sm:text-6xl lg:text-7xl text-designer-dark"
              itemProp="name"
            >
              Brands with a{" "}
              <span className="relative inline-block text-designer-primary">
                point
                <svg
                  className="absolute -bottom-1.5 left-0 w-full"
                  viewBox="0 0 220 12"
                  fill="none"
                  aria-hidden="true"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M3 9C60 3 150 3 217 8"
                    stroke="#E31C25"
                    strokeWidth="5"
                    strokeLinecap="round"
                    opacity="0.35"
                  />
                </svg>
              </span>{" "}
              of view
            </h1>
          </Reveal>

          <Reveal delay={170}>
            <p
              className="mt-6 text-base sm:text-lg text-gray-600 leading-relaxed max-w-xl"
              itemProp="description"
            >
              Arvix Creative is a full-stack design studio. From brand identity
              and UI/UX to web development and print, we craft visuals that
              communicate value and move audiences from idea to impact.
            </p>
          </Reveal>

          <Reveal delay={240}>
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
              <Link
                to="/projects"
                className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold text-sm text-white bg-designer-primary hover:bg-red-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-designer-primary/50"
                aria-label="View our projects and creative work"
              >
                View Our Work
                <FaArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" aria-hidden="true" />
              </Link>
              <Link
                to="/contact"
                className="link-sweep inline-flex items-center justify-center sm:justify-start gap-2 font-semibold text-sm text-designer-dark hover:text-designer-primary transition-colors w-fit"
                aria-label="Get started with your creative design project"
              >
                Start a project
                <span aria-hidden="true">→</span>
              </Link>
            </div>
          </Reveal>

          <Reveal delay={310}>
            <dl className="mt-10 flex items-stretch gap-6 sm:gap-8 border-t border-designer-dark/10 pt-6">
              {[
                ["120+", "Projects shipped"],
                ["85+", "Happy clients"],
                ["5+", "Years of craft"],
              ].map(([value, label]) => (
                <div key={label}>
                  <dt className="sr-only">{label}</dt>
                  <dd className="font-display font-extrabold text-2xl sm:text-3xl text-designer-dark leading-none">
                    {value}
                  </dd>
                  <dd className="mt-1.5 text-xs font-medium tracking-wide text-gray-500 uppercase">
                    {label}
                  </dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>

        {/* Visual */}
        <Reveal delay={150} className="relative flex justify-center lg:justify-end">
          <img
            src={heroImage}
            alt="Arvix Creative - Professional creative studio showcasing brand identity and creative work"
            className="w-full sm:w-full lg:w-11/12 xl:w-5/6 h-auto max-w-3xl drop-shadow-xl"
          />

          {/* Slogan: From Idea to Impact */}
          <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 lg:right-6 lg:left-auto lg:translate-x-0">
            <div className="inline-flex items-center gap-2 sm:gap-3 px-3.5 sm:px-5 py-1.5 sm:py-2 rounded-full bg-transparent whitespace-nowrap">
              <span className="uppercase tracking-[0.18em] text-[9px] sm:text-xs font-semibold text-gray-600">From</span>
              {/* Spark icon (Idea) */}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="opacity-80">
                <path d="M12 2v4M12 18v4M2 12h4M18 12h4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" stroke="#e31c25" strokeWidth="1.6" strokeLinecap="round"/>
                <circle cx="12" cy="12" r="2.5" stroke="#e31c25" strokeWidth="1.5" fill="none"/>
              </svg>
              <span className="uppercase tracking-[0.22em] text-[10px] sm:text-xs font-bold text-gray-800">Idea</span>
              <span className="mx-1 h-[2px] w-4 sm:w-6 bg-gradient-to-r from-gray-300 to-gray-400 rounded-full" aria-hidden="true"></span>
              <span className="uppercase tracking-[0.18em] text-[9px] sm:text-xs font-semibold text-gray-600">to</span>
              {/* Target icon (Impact) */}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="opacity-90">
                <circle cx="12" cy="12" r="8" stroke="#ef4444" strokeWidth="1.5"/>
                <circle cx="12" cy="12" r="4.5" stroke="#ef4444" strokeWidth="1.5"/>
                <circle cx="12" cy="12" r="2" fill="#ef4444"/>
                <path d="M19 5l-3 3M20 7l-4-0" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <span className="uppercase tracking-[0.24em] sm:tracking-[0.3em] text-[10px] sm:text-sm font-extrabold text-designer-primary">Impact</span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default HeroSection;
